import { Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';

export interface PlanLimitDialogData {
  groupId: string;
  seatsLimit?: number;
  isSubscribed?: boolean;
  actionType?: 'add_student' | 'assign_role';
  title?: string;
  message?: string;
  groupType?: string;
  actionText?: string;
  cancelText?: string;
}

@Component({
  selector: 'app-dlg-plan-limit',
  standalone: true,
  imports: [MatDialogModule],
  templateUrl: './dlg-plan-limit.component.html',
  styleUrl: './dlg-plan-limit.component.css',
})
export class DlgPlanLimitComponent {
  readonly dialogRef = inject(MatDialogRef<DlgPlanLimitComponent>);
  readonly data = inject<PlanLimitDialogData>(MAT_DIALOG_DATA);
  private router = inject(Router);

  title: string;
  message: string;
  actionText: string;
  cancelText: string;

  constructor() {
    const isSubscribed = this.data?.isSubscribed ?? false;
    const seatsLimit = this.data?.seatsLimit ?? 25;
    const actionType = this.data?.actionType ?? 'add_student';

    // Title
    this.title =
      this.data?.title ??
      (isSubscribed ? 'Plan Limit Reached' : 'Free Tier Limit Reached');

    // Message
    if (this.data?.message) {
      this.message = this.data.message;
    } else if (!isSubscribed) {
      this.message =
        actionType === 'assign_role'
          ? 'Assigning the Student role requires upgrading to a paid plan. Please upgrade your plan in the Billing tab to add more students.'
          : 'Adding a student requires upgrading to a paid plan. Please upgrade your plan in the Billing tab to add more students.';
    } else {
      if (seatsLimit === 75) {
        this.message =
          actionType === 'assign_role'
            ? 'Assigning the Student role requires upgrading to the Thrive plan (up to 150 students). Please upgrade your plan in the Billing tab to add more students.'
            : 'Adding a student requires upgrading to the Thrive plan (up to 150 students). Please upgrade your plan in the Billing tab to add more students.';
      } else if (seatsLimit === 150) {
        this.message =
          actionType === 'assign_role'
            ? 'Assigning the Student role requires upgrading to the Enterprise plan. Please upgrade your plan in the Billing tab to add more students.'
            : 'Adding a student requires upgrading to the Enterprise plan. Please upgrade your plan in the Billing tab to add more students.';
      } else {
        this.message =
          actionType === 'assign_role'
            ? `Assigning the Student role exceeds your current plan limit of ${seatsLimit} seats. Please upgrade your plan in the Billing tab to add more students.`
            : `Adding a student exceeds your current plan limit of ${seatsLimit} seats. Please upgrade your plan in the Billing tab to add more students.`;
      }
    }

    this.actionText = this.data?.actionText ?? 'Go to Billing Page';
    this.cancelText = this.data?.cancelText ?? 'Cancel';
  }

  static open(
    dialog: MatDialog,
    data: PlanLimitDialogData,
  ): MatDialogRef<DlgPlanLimitComponent, boolean> {
    const config = new MatDialogConfig<PlanLimitDialogData>();
    config.data = data;
    config.disableClose = true;
    config.autoFocus = true;
    config.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];
    return dialog.open(DlgPlanLimitComponent, config);
  }

  onGoToBilling(): void {
    if (this.data?.groupId) {
      const path =
        this.data.groupType === 'church'
          ? `/groups/${this.data.groupId}/billing`
          : `/school/${this.data.groupId}/billing`;
      this.router.navigateByUrl(path);
    }
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
